import React from 'react'
import { Fade, Popper } from '@mui/material'
import { CloseCircle } from 'iconsax-react'
import { track } from '@SonarCX/lib/mixpanel'

import CopyableStateProvider from '../../../providers/CopyableStateProvider'
import TaskComment from '../../molecules/ChatModals/TaskModal/TaskComments/TaskComment'
import MessageSkeletonV2 from '../../molecules/MessageSkeletonV2'
import TaskCommentInput from '../TaskCommentInput'

import { mockComment } from '@SonarCX/providers/TaskAPIProvider/helpers'
import { useTaskComments } from '@SonarCX/providers/TaskCommentsProvider'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import useTaskPermissions from '@SonarCX/hooks/permissions/useTaskPermissions'
import useStyles from './styles'

interface TaskCommentsPreviewModalProps {
  taskId: string
  onClose: () => void
  parentRef: any
  shouldCheckClose: boolean
  task?: LocalTask
}

const TaskCommentsPreviewModal: React.FC<TaskCommentsPreviewModalProps> = ({
  taskId,
  task,
  onClose,
  parentRef,
  shouldCheckClose
}) => {
  const {
    setAction: setCloseAction,
    setReason: setCloseReason,
    setSubtext,
    setV2
  } = useAreYouSure()
  const {
    comments,
    processCommentEvent,
    setHasModalWithComments,
    hasModalWithComments
  } = useTaskComments()
  const { up1024 } = useBreakpoint()
  const { tasks, addComment } = useTaskAPI()
  const fullTask = React.useMemo(
    () => task ?? tasks[taskId],
    [task, tasks, taskId]
  )
  const { canView } = useTaskPermissions(fullTask)
  const { user } = useEnvironment()

  const [hasBeenHovered, setHasBeenHovered] = React.useState(false)
  const [comment, setComment] = React.useState('')
  const placement = up1024 ? 'right' : 'auto'
  const classes = useStyles()
  
  const loaded = React.useMemo(() => taskId in comments, [comments, taskId])
  const sortedComments = React.useMemo(() => {
    const sorted = (
      comments[taskId] ? Object.values(comments[taskId]) : []
    ).sort((a: any, b: any) => +new Date(b.stamp) - +new Date(a.stamp))
    return sorted.map(comment => ({ type: 'comment', data: comment }))
  }, [comments, taskId])

  const handleMouseEnter = React.useCallback(() => {
    setHasBeenHovered(true)
  }, [])

  const handleMouseLeave = React.useCallback(() => {
    if (hasModalWithComments) return
    if (hasBeenHovered) {
      onClose()
    }
  }, [hasBeenHovered, hasModalWithComments, onClose])

  const handleSubmit = React.useCallback(async () => {
    const trimmed = comment.trim()

    if (trimmed.length) {
      const mocked = mockComment(trimmed, user?.id, taskId)
      processCommentEvent({ comment: mocked })
      track('TASK_COMMENT_ADDED_TO_PREVIEW', { comment: mocked })
      setComment('')
      await addComment(taskId, trimmed)
    }
  }, [comment, user, taskId, processCommentEvent, addComment])

  const handleOnClickClose = React.useCallback(() => {
    if (!hasModalWithComments) {
      onClose()
      return
    }
    setV2(true)
    setCloseReason('Are you sure you want to close this modal?')
    setSubtext('Your comment will not be sent.')
    setCloseAction(() => onClose())
  }, [
    hasModalWithComments,
    onClose,
    setCloseAction,
    setCloseReason,
    setSubtext,
    setV2
  ])

  React.useEffect(() => {
    track('TASK_COMMENTS_PREVIEW_OPENED')
  }, [])

  React.useEffect(() => {
    if (hasModalWithComments) return
    if (shouldCheckClose) {
      if (!hasBeenHovered) {
        onClose()
      }
    }
  }, [shouldCheckClose, hasBeenHovered, onClose, hasModalWithComments])

  React.useEffect(() => {
    setHasModalWithComments(comment.trim().length > 0)
  }, [setHasModalWithComments, comment])

  // if by any reason this modal was closed and the user is still typing a comment (ie:navigation to another path), set the hasModalWithComments to false
  React.useEffect(() => {
    return () => {
      setHasModalWithComments(false)
    }
  }, [setHasModalWithComments])

  return (
    <Popper
      open
      anchorEl={parentRef?.current}
      style={{ zIndex: '1299' }}
      placement={placement}
      transition
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps}>
          <div
            className={classes.root}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {hasModalWithComments && (
              <CloseCircle
                className={classes.closeBtn}
                onClick={handleOnClickClose}
              />
            )}
            {!loaded ? (
              <div>
                <MessageSkeletonV2 isYours />
                <MessageSkeletonV2 isYours={false} />
              </div>
            ) : (
              <>
                <div className={classes.comments}>
                  <CopyableStateProvider>
                    {sortedComments.map((comment: any) => (
                      <TaskComment
                        key={comment.data.id}
                        comment={comment.data}
                        inModal
                        disableEdit
                      />
                    ))}
                  </CopyableStateProvider>
                </div>

                <TaskCommentInput
                  hideAdd
                  autoFocus
                  noPortalHost
                  value={comment}
                  onChange={setComment}
                  canComment={canView}
                  submit={handleSubmit}
                  className={classes.input}
                />
              </>
            )}
          </div>
        </Fade>
      )}
    </Popper>
  )
}

export default TaskCommentsPreviewModal
